import { useQuery } from "@tanstack/react-query";
import api from "../../../utils/api";
import { pollingRefetchInterval } from "../../../utils/requestFailure";
import type { BoardItem, BoardMeta, MentionableUser, TicketDetail } from "../types";

export const BOARD_KEY = ["dev-board", "board"];
const DONE_KEY = ["dev-board", "board", "done"];
const META_KEY = ["dev-board", "meta"];
const MENTIONABLES_KEY = ["dev-board", "mentionables"];

/** Fires after mentions are acknowledged so the sidebar badge and the mention
 * toaster drop the count without waiting for their next poll. */
export const DEV_BOARD_MENTIONS_READ_EVENT = "devBoardMentionsRead";
export const ticketKey = (issueNumber: number) => ["dev-board", "ticket", issueNumber];

/** The column deferred out of the initial load: ~85% of the project's items sit
 * in Done, and nobody scrolls there first. Hardcoding the name is safe — if the
 * column is renamed, `exclude` stops matching and the main query simply returns
 * everything again, while `only` returns an empty list the merge ignores. */
export const DONE_STATUS = "Done";

export interface BoardPayload {
  items: BoardItem[];
  fetchedAt: string;
}

/** Everything except Done — what the board needs to paint first. */
export function useBoardQuery() {
  return useQuery<BoardPayload>({
    queryKey: BOARD_KEY,
    queryFn: async () =>
      (await api.get(`/api/v1/dev-board/board?exclude=${encodeURIComponent(DONE_STATUS)}`)).data,
    refetchInterval: (query) => pollingRefetchInterval(query, 90_000),
  });
}

/** The Done column, fetched behind the first paint. Server-side both halves come
 * from the same cache, so this costs one extra round trip, not a second rebuild. */
export function useDoneColumnQuery(enabled: boolean) {
  return useQuery<BoardPayload>({
    queryKey: DONE_KEY,
    queryFn: async () =>
      (await api.get(`/api/v1/dev-board/board?only=${encodeURIComponent(DONE_STATUS)}`)).data,
    enabled,
    refetchInterval: (query) => pollingRefetchInterval(query, 5 * 60_000),
  });
}

/**
 * One list out of the two halves. The main board wins a collision: a card
 * dragged into Done is patched in the main cache long before the Done query
 * refetches, and showing both would duplicate it.
 */
export function mergeBoardItems(main?: BoardPayload, done?: BoardPayload): BoardItem[] {
  const items = main?.items ?? [];
  if (!done?.items?.length) return items;
  const seen = new Set(items.map((item) => item.itemId));
  return [...items, ...done.items.filter((item) => !seen.has(item.itemId))];
}

/** Sort rank for a Priority value: P0 first, unprioritised last. Non-`P<n>`
 * values sort with the unprioritised rather than guessing an order for them. */
export function priorityRank(priority: string | null | undefined): number {
  const match = /^P(\d+)$/i.exec(String(priority ?? "").trim());
  return match ? Number(match[1]) : Number.MAX_SAFE_INTEGER;
}

export function useBoardMetaQuery() {
  return useQuery<BoardMeta>({
    queryKey: META_KEY,
    queryFn: async () => (await api.get("/api/v1/dev-board/meta")).data,
    staleTime: 10 * 60_000,
  });
}

/** Admins taggable in a comment. Separate from `meta` on purpose: `meta` needs
 * GitHub, and losing the mention picker to a GitHub outage would be silly. */
export function useMentionablesQuery(enabled = true) {
  return useQuery<MentionableUser[]>({
    queryKey: MENTIONABLES_KEY,
    queryFn: async () => (await api.get("/api/v1/dev-board/mentionables")).data.users,
    enabled,
    staleTime: 10 * 60_000,
  });
}

export function useTicketQuery(issueNumber: number | null) {
  return useQuery<TicketDetail>({
    queryKey: ticketKey(issueNumber ?? -1),
    queryFn: async () => (await api.get(`/api/v1/dev-board/tickets/${issueNumber}`)).data,
    enabled: issueNumber != null,
  });
}

export async function uploadTicketImage(file: File): Promise<string> {
  const formData = new FormData();
  formData.append("image", file);
  const response = await api.post("/api/v1/dev-board/images", formData, {
    headers: { "Content-Type": "multipart/form-data" },
  });
  return response.data.url as string;
}
